import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { MutationKeys } from 'src/constants';
import ApiService from 'src/modules/api-service';
import { StartMfaLoginRequestDto } from 'src/modules/generated/api';
import { MfaLoginResult, mfaLoginResultsState } from 'src/stores/mfa-login';

/**
 * The backend answers a failed login with the reason in the HTTP status (openapi.json). 401 is
 * deliberately absent: it means our own session died and the session interceptor handles it, so it
 * never reaches this mapping.
 */
const ERROR_KEYS = {
  400: 'dealerManagementPage.mfaLogin.errors.notSupported',
  404: 'dealerManagementPage.mfaLogin.errors.noCredentials',
  409: 'dealerManagementPage.mfaLogin.errors.ambiguousCredentials',
  422: 'dealerManagementPage.mfaLogin.errors.failed',
  502: 'dealerManagementPage.mfaLogin.errors.notStarted',
} as const;

export const mfaLoginErrorKey = (httpStatus?: number) =>
  ERROR_KEYS[httpStatus as keyof typeof ERROR_KEYS] ?? 'dealerManagementPage.mfaLogin.errors.generic';

/** The last terminal outcome for a credential, or `undefined` while none has been recorded. */
export const useMfaLoginResult = (credId: string) => useRecoilValue(mfaLoginResultsState)[credId];

type UseMfaLoginOptions = {
  onSuccess?: () => void;
  onError?: (result: MfaLoginResult) => void;
};

export const useMfaLogin = (credId: string, { onSuccess, onError }: UseMfaLoginOptions = {}) => {
  const setResults = useSetRecoilState(mfaLoginResultsState);

  const remember = (result: MfaLoginResult) => setResults((results) => ({ ...results, [credId]: result }));

  return useMutation((body: StartMfaLoginRequestDto) => ApiService.mfaLogin.mfaLoginControllerStartLogin(body), {
    mutationKey: [MutationKeys.mfaLogin, credId],
    onMutate: () => {
      setResults(({ [credId]: _previous, ...others }) => others);
    },
    onSuccess: () => {
      remember({ status: 'success', finishedAt: new Date().toISOString() });
      onSuccess?.();
    },
    onError: (error) => {
      const result: MfaLoginResult = {
        status: 'error',
        httpStatus: axios.isAxiosError(error) ? error.response?.status : undefined,
        finishedAt: new Date().toISOString(),
      };

      remember(result);
      onError?.(result);
    },
  });
};
